import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { styled } from '@this/constants/themes';
import { toDomesticTel } from '@this/src/util';
import { isMatchingTraceId } from '@this/src/util/string/string.util';
import CancelLimitForm from '@this/components/arrangement/shared/price_change_form/price_change_form_item/cancel_limit_form/cancel_limit_form';
import type HotelElement from '@this/domain/hotel_element2';
import type OrderItem from '@this/domain/order_item2';
import DatetimePicker from '../../../../../shared/datetime_picker/datetime_picker';

interface Props {
  element: HotelElement;
  orderItem: OrderItem;
  providerOptions: { [key: string]: string };
  classNameForModifiedField: (...path: (string | number)[]) => string;
  handleIsForeignChange: (element: HotelElement, address: string) => void;
  handleIsForeignLoad: (element: HotelElement) => void;
}

const HotelElementForm = ({
  element,
  orderItem,
  providerOptions,
  classNameForModifiedField,
  handleIsForeignChange,
  handleIsForeignLoad
}: Props) => {
  const numArray: number[] = [];
  for (let i = 1; i < 10; i += 1) {
    numArray.push(i);
  }

  useEffect(() => {
    handleIsForeignLoad(element);
  }, []);

  return (
    <>
      <div className="virtual-counte-price-change-form__input-wrapper">
        <label className={`virtual-counte-price-change-form__label ${classNameForModifiedField('provider')}`}>
          提供元
        </label>
        <select
          value={element.provider}
          onChange={e => {
            element.provider = e.target.value;
          }}
        >
          {Object.keys(providerOptions).map(key => (
            <option key={key} value={key}>
              {providerOptions[key]}
            </option>
          ))}
          {element.initialData?.provider === 'mynavi' && <option value="mynavi">マイナビ</option>}
        </select>
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper">
        <label className={`virtual-counte-price-change-form__label ${classNameForModifiedField('roomNum')}`}>
          部屋数
        </label>
        <select
          value={element.roomNum}
          onChange={e => {
            element.roomNum = parseInt(e.target.value, 10);
          }}
        >
          {numArray.map(i => (
            <option key={i} value={i}>
              {i}
            </option>
          ))}
        </select>
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper">
        <label className={`virtual-counte-price-change-form__label ${classNameForModifiedField('name')}`}>
          ホテル名
          <span className="red">（必須）</span>
        </label>
        <input
          type="text"
          value={element.name}
          onChange={e => {
            element.name = e.target.value;
          }}
        />
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper">
        <label className={`virtual-counte-price-change-form__label ${classNameForModifiedField('address')}`}>
          住所
        </label>
        <input
          type="text"
          value={element.address}
          onChange={e => {
            element.address = e.target.value;
          }}
          onBlur={e => handleIsForeignChange(element, e.target.value)}
        />
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper">
        <label className={`virtual-counte-price-change-form__label ${classNameForModifiedField('isForeign')}`}>
          海外 / 国内
        </label>
        <select
          value={element.isForeign ? 'true' : 'false'}
          onChange={e => {
            element.isForeign = e.target.value === 'true';
          }}
        >
          <option value="false">国内</option>
          <option value="true">海外</option>
        </select>
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper">
        <label className={`virtual-counte-price-change-form__label ${classNameForModifiedField('tel')}`}>
          電話番号
        </label>
        <input
          type="text"
          value={toDomesticTel(element.tel || '')}
          onChange={e => {
            element.tel = e.target.value;
          }}
        />
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper">
        <label className={`virtual-counte-price-change-form__label ${classNameForModifiedField('planName')}`}>
          宿泊プラン
        </label>
        <input
          type="text"
          value={element.planName}
          onChange={e => {
            element.planName = e.target.value;
          }}
        />
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper">
        <label className={`virtual-counte-price-change-form__label ${classNameForModifiedField('roomType')}`}>
          部屋タイプ
        </label>
        <input
          type="text"
          value={element.roomType}
          onChange={e => {
            element.roomType = e.target.value;
          }}
        />
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper">
        <label className={`virtual-counte-price-change-form__label ${classNameForModifiedField('bedType')}`}>
          ベッドタイプ
        </label>
        <input
          type="text"
          value={element.bedType ?? undefined}
          onChange={e => {
            element.bedType = e.target.value;
          }}
        />
        {element.bedTypeNoticeMessage && <div style={BedTypeNoticeStyle}>{element.bedTypeNoticeMessage}</div>}
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper">
        <label className={`virtual-counte-price-change-form__label ${classNameForModifiedField('breakfast')}`}>
          朝食
        </label>
        <select
          value={element.breakfast === true ? 'true' : element.breakfast === false ? 'false' : 'null'}
          onChange={e => {
            element.breakfast = e.target.value === 'true' ? true : e.target.value === 'false' ? false : undefined;
          }}
        >
          <option value="true">有り</option>
          <option value="false">無し</option>
          <option value="null">問い合わせ</option>
        </select>
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper">
        <label className={`virtual-counte-price-change-form__label ${classNameForModifiedField('refundable')}`}>
          返金
        </label>
        <select
          value={element.refundable === true ? 'true' : element.refundable === false ? 'false' : 'null'}
          onChange={e => {
            element.refundable = e.target.value === 'true' ? true : e.target.value === 'false' ? false : undefined;
          }}
        >
          <option value="true">可</option>
          <option value="false">不可</option>
          <option value="null">問い合わせ</option>
        </select>
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper-item">
        <label className={`virtual-counte-price-change-form__label ${classNameForModifiedField('checkinDate')}`}>
          チェックイン
        </label>
        <DatetimePicker
          value={element.checkinDate}
          onChange={time => {
            element.checkinDate = time;
          }}
          showTime={false}
          disabledDays={0}
          showPast
        />
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper-item">
        <label className={`virtual-counte-price-change-form__label ${classNameForModifiedField('checkoutDate')}`}>
          チェックアウト
        </label>
        <DatetimePicker
          value={element.checkoutDate}
          onChange={time => {
            element.checkoutDate = time;
          }}
          showTime={false}
          disabledDays={0}
          showPast
        />
      </div>
      <CancelLimitForm
        hotelElement={element}
        checkinDate={element.checkinDate}
        classNameForModifiedField={classNameForModifiedField}
      />
      <div className="virtual-counte-price-change-form__input-wrapper">
        <StyledGuestNameLabel
          className={`virtual-counte-price-change-form__label ${classNameForModifiedField('guestLastName')}`}
        >
          宿泊代表者（ローマ字）
        </StyledGuestNameLabel>
        <input
          type="text"
          value={element.guestLastName}
          onChange={e => {
            element.guestLastName = e.target.value;
          }}
        />
        <input
          type="text"
          value={element.guestFirstName}
          onChange={e => {
            element.guestFirstName = e.target.value;
          }}
        />
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper">
        <StyledGuestNameLabel
          className={`virtual-counte-price-change-form__label ${classNameForModifiedField('guestLastNameKana')}`}
        >
          宿泊代表者（カナ）
        </StyledGuestNameLabel>
        <input
          type="text"
          value={element.guestLastNameKana}
          onChange={e => {
            element.guestLastNameKana = e.target.value;
          }}
        />
        <input
          type="text"
          value={element.guestFirstNameKana}
          onChange={e => {
            element.guestFirstNameKana = e.target.value;
          }}
        />
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper">
        <StyledGuestNameLabel
          className={`virtual-counte-price-change-form__label ${classNameForModifiedField('guestTel')}`}
        >
          宿泊代表者電話番号
        </StyledGuestNameLabel>
        <input
          type="text"
          value={toDomesticTel(element.guestTel || '')}
          onChange={e => {
            element.guestTel = e.target.value;
          }}
        />
      </div>
      <div className="virtual-counte-price-change-form__input-wrapper">
        <label
          className={`virtual-counte-price-change-form__label ${classNameForModifiedField(
            'hotelReservationNumber'
          )}`}
        >
          ホテル予約番号
        </label>
        <input
          type="text"
          value={element.hotelReservationNumber ?? undefined}
          onChange={e => {
            element.hotelReservationNumber = e.target.value;
          }}
          style={{
            backgroundColor: isMatchingTraceId(element.hotelReservationNumber, orderItem.traceId)
              ? '#ffebee'
              : undefined,
            borderColor: isMatchingTraceId(element.hotelReservationNumber, orderItem.traceId)
              ? '#f8bbd9'
              : undefined
          }}
        />
      </div>
    </>
  );
};

const StyledGuestNameLabel = styled.label`
  width: 150px;
`;

const BedTypeNoticeStyle = {
  marginTop: '4px',
  padding: '8px',
  backgroundColor: '#fff3cd',
  border: '1px solid #ffeaa7',
  borderRadius: '4px',
  fontSize: '12px',
  color: '#856404'
} as const;

export default observer(HotelElementForm);
